import { Box, Typography, Link } from '@mui/material';

const NoDataFallback = ({
  message,
  description,
  httpStatusCode,
  clickableWords = {},
}: {
  message?: string;
  description?: string;
  httpStatusCode?: number;
  clickableWords?: Record<string, string>; // word to URL mapping
}) => {
  const getErrorMessage = (code: number) => {
    switch (code) {
      case 404:
        return "Oops! We couldn't find what you were looking for.";
      case 500:
        return 'Something went wrong on our end. Please try again later.';
      case 403:
        return "You don't have permission to do that.";
      case 400:
        return '';
      default:
        return 'An unexpected error occurred.';
    }
  };

  const getErrorDescription = (code: number) => {
    switch (code) {
      case 404:
        return 'The resource you are looking for might have been removed, had its name changed, or is temporarily unavailable.';
      case 500:
        return 'Our team has been notified and is working on a fix.';
      case 403:
        return 'Please contact support if you believe this is a mistake.';
      default:
        return 'Please try again later.';
    }
  };

  const renderDescription = (text: string) => {
    const words = text.split(' ');
    return words.map((word, index) => {
      const cleanWord = word.replace(/[.,!?]/g, ''); // Remove punctuation
      if (clickableWords[cleanWord]) {
        return (
          <Link
            key={index}
            href={clickableWords[cleanWord]}
            sx={{
              color: 'primary.main',
              textDecoration: 'none',
              fontWeight: 'bold',
            }}
          >
            {word}
          </Link>
        );
      }
      return ` ${word} `;
    });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        p: 3,
        textAlign: 'center',
        width: '100%',
      }}
    >
      <Typography
        variant="h5"
        gutterBottom
        sx={{ fontSize: '20px', textAlign: 'center' }}
      >
        {message ||
          (httpStatusCode ? getErrorMessage(httpStatusCode) : 'Error')}
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        align="center"
        sx={{ fontSize: '14px' }}
      >
        {description
          ? renderDescription(description)
          : httpStatusCode
            ? getErrorDescription(httpStatusCode)
            : 'An error occurred.'}
      </Typography>
    </Box>
  );
};

export default NoDataFallback;
